import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Loader2, ArrowRight, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import Reveal from "@/components/Reveal";
import { SITE } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const initial = { name: "", email: "", phone: "", subject: "", message: "" };

const SUBJECT_BY_SERVICE = {
  "capacity-building": "NDIS — Register interest in Capacity Building Services",
  "community-participation": "NDIS — Register interest in Community Participation Services",
  "ndis": "NDIS — General registration of interest",
};

export default function Contact() {
  const [params] = useSearchParams();
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const service = params.get("service");
    if (service && SUBJECT_BY_SERVICE[service]) {
      setForm((f) => ({ ...f, subject: SUBJECT_BY_SERVICE[service] }));
    }
  }, [params]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success("Message sent. We'll reply within one business day.");
      setForm(initial);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const items = [
    { icon: Phone, label: "Call us", value: SITE.phone, href: `tel:${SITE.phone.replace(/\s/g, "")}` },
    { icon: Mail, label: "Email", value: SITE.email, href: `mailto:${SITE.email}` },
    { icon: MapPin, label: "Visit", value: SITE.address },
    { icon: Clock, label: "Hours", value: SITE.hours },
  ];

  return (
    <div data-testid="page-contact">
      <section className="bg-warm-50 pt-24 pb-16 lg:pt-32 lg:pb-20">
        <div className="max-w-6xl mx-auto px-6 md:px-10 lg:px-12">
          <p className="text-xs uppercase tracking-[0.25em] text-brand font-semibold mb-6">Contact</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.02] text-ink-900 max-w-4xl">
            Talk to a human, not a queue.
          </h1>
          <p className="mt-7 text-lg lg:text-xl leading-relaxed text-ink-500 max-w-2xl">
            For a referral, intake question, or general enquiry — reach us directly. A clinician
            answers our intake line during business hours.
          </p>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-5">
            <ul className="space-y-5">
              {items.map((it, i) => (
                <li
                  key={i}
                  data-testid={`contact-item-${it.label.toLowerCase()}`}
                  className="flex items-start gap-5 p-6 rounded-2xl bg-warm-50 border border-warm-200/60"
                >
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand/10 text-brand shrink-0">
                    <it.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-ink-400 mb-1">{it.label}</p>
                    {it.href ? (
                      <a href={it.href} className="text-ink-900 font-medium hover:text-brand transition-colors">
                        {it.value}
                      </a>
                    ) : (
                      <p className="text-ink-900 font-medium">{it.value}</p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </Reveal>

          <div className="lg:col-span-7">
            {done ? (
              <div
                data-testid="contact-success"
                className="rounded-2xl bg-warm-50 border border-warm-200/60 p-12 text-center"
              >
                <CheckCircle2 className="w-12 h-12 text-brand mx-auto mb-4" />
                <h3 className="font-serif text-3xl text-ink-900 mb-2">Message sent</h3>
                <p className="text-ink-500">We'll reply within one business day.</p>
                <button
                  onClick={() => setDone(false)}
                  data-testid="send-another"
                  className="mt-6 inline-flex items-center gap-2 text-brand font-semibold"
                >
                  Send another <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <form
                onSubmit={submit}
                data-testid="contact-form"
                className="rounded-2xl border border-warm-200/60 p-8 lg:p-10 space-y-5 bg-white"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <FormField label="Your name *" id="c-name">
                    <Input id="c-name" data-testid="input-contact-name" value={form.name} onChange={set("name")} required />
                  </FormField>
                  <FormField label="Email *" id="c-email">
                    <Input id="c-email" type="email" data-testid="input-contact-email" value={form.email} onChange={set("email")} required />
                  </FormField>
                  <FormField label="Phone" id="c-phone">
                    <Input id="c-phone" data-testid="input-contact-phone" value={form.phone} onChange={set("phone")} />
                  </FormField>
                  <FormField label="Subject" id="c-subject">
                    <Input id="c-subject" data-testid="input-contact-subject" value={form.subject} onChange={set("subject")} />
                  </FormField>
                </div>
                <FormField label="Message *" id="c-message">
                  <Textarea id="c-message" rows={6} data-testid="textarea-contact-message" value={form.message} onChange={set("message")} required />
                </FormField>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="submit-contact"
                  className="inline-flex items-center gap-2 rounded-full bg-brand text-white px-7 py-4 text-sm font-semibold hover:bg-brand-hover disabled:opacity-60 transition-colors"
                >
                  {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>Send message <ArrowRight className="w-4 h-4" /></>}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function FormField({ label, id, children }) {
  return (
    <div>
      <Label htmlFor={id} className="text-sm text-ink-700 mb-2 block font-medium">{label}</Label>
      {children}
    </div>
  );
}
